import { useIntervalEffect } from "@react-hookz/web";
import YouTube from "@vip30/react-youtube";
import { useAtom } from "jotai";
import { useState } from "react";
import type { YouTubePlayer } from "youtube-player/dist/types";

import { scmpYouTubeVideoAtom } from "~/components/scmp-youtube-video/atoms";
import { AutoplayMode } from "~/components/scmp-youtube-video/scmp-youtube-video-player/enums";
import { pushPlayer as chartbeatPushPlayer } from "~/components/tracking/chartbeat";
import { sendVideoTracking } from "~/components/tracking/google-tag-manager/apis";
import { add as mirrorAdd, load as mirrorLoad } from "~/components/tracking/mirror";
import { sendOnePlusXEvent } from "~/components/tracking/one-plus-x";

import type { TrackingData } from "./types";

/**
 * For supporting youtube player control
 */
export const useYouTubeControl = (uniqueVideoId: string, trackingData: TrackingData) => {
  const [autoplayMode, setAutoplayMode] = useState<AutoplayMode>(AutoplayMode.Unset);
  const [manualPaused, setManualPaused] = useState(false);
  const [player, setPlayer] = useState<OrUndefined<YouTubePlayer>>();
  const [scmpYouTubeVideo, setScmpYouTubeVideo] = useAtom(scmpYouTubeVideoAtom);

  const [hasTriggeredThreeSecond, setHasTriggeredThreeSecond] = useState(false);

  const gaLabel = `${trackingData.youTubeVideoTitle} - ${trackingData.youTubeVideoId}`;
  const onePlusVideoVertical = trackingData.sectionString;

  useIntervalEffect(
    async () => {
      const currentTime = await player?.getCurrentTime();

      if (currentTime && currentTime > 3) {
        sendVideoTracking({
          action: `${trackingData.gaActionPrefix}Video progress 3s`,
          category: "Youtube video",
          label: gaLabel,
        });

        setHasTriggeredThreeSecond(true);
      }
    },
    scmpYouTubeVideo.playingId && !hasTriggeredThreeSecond ? 500 : undefined,
  );

  const [lastSeenPercentage, setLastSeenPercentage] = useState(0);

  useIntervalEffect(
    async () => {
      const durationOfVideo = await player?.getDuration();
      const currentTime = await player?.getCurrentTime();

      if (!currentTime || !durationOfVideo) return;

      const percentageElapsed = Math.round((currentTime / durationOfVideo) * 100);

      if (
        percentageElapsed > 0 &&
        percentageElapsed % 10 === 0 &&
        percentageElapsed !== lastSeenPercentage
      ) {
        sendVideoTracking({
          action: `${trackingData.gaActionPrefix}Video progress ${percentageElapsed}%`,
          category: "Youtube video",
          label: gaLabel,
        });

        sendOnePlusXEvent({
          action: `Video Progress ${percentageElapsed}%`,
          type: "video-plays",
          videoVertical: onePlusVideoVertical,
        });

        setLastSeenPercentage(percentageElapsed);
      }
    },
    scmpYouTubeVideo.playingId && lastSeenPercentage !== 100 ? 500 : undefined,
  );

  const handleOnEnd = () => {
    setPlayer(undefined);
  };

  const handleOnReady = (event: { target: YouTubePlayer }) => {
    setPlayer(event.target);
    chartbeatPushPlayer(event.target);
    mirrorAdd(event.target, "yt");
    mirrorLoad("", "ad");
  };

  /**
   * Calling player.pauseVideo will also trigger onPause
   * if autoPaused is true the event should be triggered by intersection observer
   */
  const handleOnPause = () => {
    if (!player) return;
    // Ref: it can be paused in the beginning because of https://developer.chrome.com/blog/autoplay/#iframe-delegation
    if (autoplayMode === AutoplayMode.Pausing) {
      setManualPaused(false);
    } else {
      setManualPaused(true);
    }
  };

  const handleOnStateChange = ({ data }: { data: number }) => {
    switch (data) {
      case YouTube.PlayerState.PLAYING:
        if (uniqueVideoId !== scmpYouTubeVideo.playingId) {
          setScmpYouTubeVideo({
            playingId: uniqueVideoId,
          });

          sendVideoTracking({
            action: `${trackingData.gaActionPrefix}Play`,
            category: "Youtube video",
            label: gaLabel,
          });

          sendOnePlusXEvent({
            action: "Play",
            type: "video-plays",
            videoVertical: onePlusVideoVertical,
          });
        }

        break;
      case YouTube.PlayerState.PAUSED:
        if (uniqueVideoId === scmpYouTubeVideo.playingId) {
          setScmpYouTubeVideo({
            playingId: undefined,
          });

          sendVideoTracking({
            action: `${trackingData.gaActionPrefix}Pause`,
            category: "Youtube video",
            label: gaLabel,
          });
        }
        break;
      case YouTube.PlayerState.ENDED:
        // TODO: close
        break;
    }
  };

  return {
    handleOnEnd,
    handleOnPause,
    handleOnReady,
    handleOnStateChange,
    manualPaused,
    player,
    setAutoplayMode,
    setManualPaused,
  };
};
